import { CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningOctagonIcon, XIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { ReactNode, useRef, useState } from 'react';
import { Button } from 'ui-library/components/Button/Button';
import { IconButton } from 'ui-library/components/IconButton/IconButton';
import s from './Toast.module.scss';

type ToastPosition = 'top' | 'bottom' | 'inline';
type TToastVariant = 'dark' | 'light';
type TToastSize = 's' | 'm' | 'l';
type TToastSeverity = 'success' | 'warning' | 'error' | 'info';

interface IToastProps {
  title: string;
  description?: string;
  severity?: TToastSeverity;
  variant?: TToastVariant;
  size?: TToastSize;
  position?: ToastPosition;
  icon?: ReactNode;
  showIcon?: boolean;
  primaryActionLabel?: string;
  primaryActionIcon?: ReactNode;
  onPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  actions?: ReactNode;
  dismissable?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const ICON_BY_SEVERITY: Record<TToastSeverity, ReactNode> = {
  success: <CheckCircleIcon weight="fill" size={16} />,
  warning: <WarningCircleIcon weight="fill" size={16} />,
  error: <WarningOctagonIcon weight="fill" size={16} />,
  info: <InfoIcon weight="fill" size={16} />,
};

const EXIT_MS = 230;
const EXIT_TRANSITION = `opacity ${EXIT_MS}ms ease-out, transform ${EXIT_MS}ms ease-out, filter ${EXIT_MS}ms ease-out, margin ${EXIT_MS}ms ease-out`;

export const Toast = ({
  title,
  description,
  severity = 'info',
  variant = 'dark',
  size = 'm',
  position = 'bottom',
  icon,
  showIcon = true,
  primaryActionLabel,
  primaryActionIcon,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  actions,
  dismissable = true,
  onDismiss,
  className,
}: IToastProps) => {
  'use memo';

  const ref = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(true);
  const isExitingRef = useRef(false);

  if (!isMounted) return null;

  const handleDismiss = () => {
    if (isExitingRef.current) return;
    isExitingRef.current = true;

    const el = ref.current;
    if (!el) {
      setIsMounted(false);
      onDismiss?.();
      return;
    }

    el.style.animation = 'none';

    const h = el.offsetHeight;
    const parent = el.parentElement;
    const gap = parent ? parseFloat(getComputedStyle(parent).rowGap || '0') || 0 : 0;
    void el.getBoundingClientRect();

    requestAnimationFrame(() => {
      el.style.transition = EXIT_TRANSITION;
      el.style.opacity = '0';
      el.style.transform = 'translateY(-16px) scale(0.96)';
      el.style.filter = 'blur(1px)';
      el.style.marginBottom = `-${h + gap}px`;
    });

    window.setTimeout(() => {
      setIsMounted(false);
      onDismiss?.();
    }, EXIT_MS);
  };

  const resolvedIcon = icon ?? ICON_BY_SEVERITY[severity];
  const hasActions = Boolean(actions || primaryActionLabel || secondaryActionLabel || dismissable);
  const isInline = position === 'inline';
  const entryClass = position === 'top' ? s.fromTop : s.fromBottom;
  const wrapperClass = position === 'top' ? s.containerTop : s.containerBottom;
  const actionSize = size === 'l' ? 'md' : 'sm';

  const toast = (
    <div
      ref={ref}
      className={classNames(s.toast, s[variant], s[size], entryClass, isInline && s.inline, className)}
      data-theme={variant === 'dark' ? 'dark' : undefined}
      role="status"
      aria-live="polite"
    >
      <div className={s.content}>
        {showIcon && resolvedIcon && <div className={classNames(s.iconContainer, s[severity])}>{resolvedIcon}</div>}
        <div className={s.textContainer}>
          <div className={s.title}>{title}</div>
          {description && <div className={s.description}>{description}</div>}
        </div>
      </div>
      {hasActions && (
        <div className={s.actions}>
          {actions}
          {secondaryActionLabel && onSecondaryAction && (
            <Button variant="secondary" size={actionSize} onClick={onSecondaryAction}>
              {secondaryActionLabel}
            </Button>
          )}
          {primaryActionLabel && onPrimaryAction && (
            <Button variant="primary" size={actionSize} startAdornment={primaryActionIcon} onClick={onPrimaryAction}>
              {primaryActionLabel}
            </Button>
          )}
          {dismissable && (
            <IconButton
              variant="clear"
              size={actionSize}
              className={s.dismissButton}
              onClick={handleDismiss}
              aria-label="Dismiss"
            >
              <XIcon />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );

  if (isInline) return toast;
  return <div className={classNames(s.container, wrapperClass)}>{toast}</div>;
};
